import { useCallback, useEffect, useState } from 'react';
import { Hash, Info } from 'lucide-react';
import type { Me } from '../api/auth';
import { channelSettingsApi } from '../api/conversations';
import type { SuggestedChannel } from '../api/conversations';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';

/**
 * Which standing channels a unit wants.
 *
 * Channels used to be something that happened to a unit once, at founding, and never again — wrong
 * in both directions. A unit founded before the provisioning existed had none and no way to get
 * any; a unit that did not want a leaders' channel was stuck with one. They are *suggested* now,
 * and this is where a unit decides.
 *
 * Turning one off archives it: off the lists, still readable, still holding what was said (rule 6).
 * Turning it back on restores that same thread rather than making a second, because its history is
 * the unit's and a channel that came back empty would read as the history having been thrown away.
 */
export function ChannelSettingsScreen({
  unitId,
  unitName,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [rows, setRows] = useState<SuggestedChannel[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows((await channelSettingsApi.suggested(unitId)).rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this unit’s channels.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(audience: string, enabled: boolean) {
    setBusy(audience);
    setError(null);
    try {
      setRows((await channelSettingsApi.setEnabled(unitId, audience, enabled)).rows);
    } catch (e) {
      // A refusal is the domain's answer, not a crash: the unit asked for something its roster
      // cannot staff yet, and the message says which and why.
      setError(e instanceof Error ? e.message : 'Could not change that channel.');
      await load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-3)' }}
            >
              Channels
            </h1>
            <p className="t-body" style={{ color: 'var(--color-text-secondary)', maxWidth: '44ch', margin: 0 }}>
              Standing conversations your unit can have. Turning one off keeps everything said in it
              — it comes back where you left it.
            </p>

            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="secondary" onClick={() => onNavigate(`/units/${unitId}/conversations`)}>
                Back to conversations
              </Button>
            </div>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div className="flex flex-col" style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-3)' }}>
          {error && (
            <p className="t-body" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
              {error}
            </p>
          )}

          {rows === null && !error && (
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              Loading&hellip;
            </p>
          )}

          {rows?.map((row) => (
            <div
              key={row.audience}
              className="flex items-start justify-between gap-4"
              style={{
                background: 'var(--color-surface-raised)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--elevation-raised)',
                padding: 'var(--space-4)',
              }}
            >
              <div className="min-w-0">
                <span
                  className="t-body-strong inline-flex items-center gap-2"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Hash size={15} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
                  {row.title}
                </span>

                <p
                  className="t-caption"
                  style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
                >
                  {row.description}
                </p>

                {/* The domain's own reason, not a paraphrase. A unit told only that something is
                    unavailable has been told about a rule; this says which and what to do. */}
                {row.unavailable && row.reason && (
                  <p
                    className="t-caption inline-flex items-start gap-1"
                    style={{ color: 'var(--color-status-blocked)', margin: 'var(--space-2) 0 0' }}
                  >
                    <Info size={13} aria-hidden="true" style={{ marginTop: '0.15em', flexShrink: 0 }} />
                    <span>{row.reason}</span>
                  </p>
                )}
              </div>

              {row.unavailable ? (
                // No control at all rather than a dead one. `Button` makes an explanation mandatory
                // for a disabled control, and the honest explanation is already on the row above.
                <span
                  className="t-caption"
                  style={{ color: 'var(--color-text-muted)', flexShrink: 0, alignSelf: 'center' }}
                >
                  Not available yet
                </span>
              ) : (
                <Button
                  variant={row.enabled ? 'secondary' : 'primary'}
                  onClick={() => void toggle(row.audience, !row.enabled)}
                  disabledReason={busy === row.audience ? 'Saving' : undefined}
                >
                  {busy === row.audience ? 'Saving…' : row.enabled ? 'Turn off' : 'Turn on'}
                </Button>
              )}
            </div>
          ))}

          {/* Said here because it is the obvious next question, and the answer is a decision
              rather than an omission: a group's channel follows its group. */}
          <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
            Each of your groups has its own channel. Those follow the group — they appear when you
            add one and stay when you retire it.
          </p>
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
